import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Strict route gate for the operator-only `/platform/*` surface.
 *
 * Behaviour:
 *   - Loading (auth not resolved)        → spinner
 *   - Logged out                          → /login (preserves origin)
 *   - Wrong role (admin/manager/employee) → /404-like silent redirect
 *                                           to their normal landing
 *                                           page so the operator
 *                                           surface stays invisible
 *                                           to customers.
 *   - role === "platform_superadmin"      → render children
 *
 * The backend is the real security boundary
 * (``require_platform_superadmin`` + feature flag); this gate is UX
 * only — it keeps customer roles from seeing broken pages or being
 * tempted by a URL they shouldn't follow.
 */
export default function PlatformProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-paper"
        data-testid="platform-auth-loading"
      >
        <div className="font-mono text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user.role !== "platform_superadmin") {
    // Customers should never know this surface exists. Send them to
    // their normal landing so the URL behaves like a 404.
    const dest =
      user.role === "admin" || user.role === "manager" ? "/admin" : "/scan";
    return <Navigate to={dest} replace data-testid="platform-route-blocked" />;
  }

  return children;
}
